export type GL = WebGL2RenderingContext

export class ShaderError extends Error {}

function annotate(source: string): string {
  return source
    .split('\n')
    .map((line, i) => `${String(i + 1).padStart(4, ' ')} | ${line}`)
    .join('\n')
}

function compile(gl: GL, type: number, source: string, label: string): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new ShaderError(`${label}: could not allocate shader`)

  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '(no log)'
    gl.deleteShader(shader)
    throw new ShaderError(`${label} failed to compile:\n${log}\n\n${annotate(source)}`)
  }
  return shader
}

/** A linked program with a lazily-populated uniform location cache. */
export class Program {
  readonly handle: WebGLProgram
  private readonly gl: GL
  private readonly locations = new Map<string, WebGLUniformLocation | null>()

  constructor(gl: GL, vertexSource: string, fragmentSource: string, label: string) {
    this.gl = gl

    const vertex = compile(gl, gl.VERTEX_SHADER, vertexSource, `${label} (vertex)`)
    const fragment = compile(gl, gl.FRAGMENT_SHADER, fragmentSource, `${label} (fragment)`)

    const handle = gl.createProgram()
    if (!handle) throw new ShaderError(`${label}: could not allocate program`)

    gl.attachShader(handle, vertex)
    gl.attachShader(handle, fragment)
    gl.linkProgram(handle)

    // Shaders are reference-counted by the program, so they can go now.
    gl.deleteShader(vertex)
    gl.deleteShader(fragment)

    if (!gl.getProgramParameter(handle, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(handle) ?? '(no log)'
      gl.deleteProgram(handle)
      throw new ShaderError(`${label} failed to link:\n${log}`)
    }

    this.handle = handle
  }

  use(): void {
    this.gl.useProgram(this.handle)
  }

  /**
   * Uniform location, cached. Returns null for uniforms the compiler
   * optimised away, which `gl.uniform*` accepts as a no-op.
   */
  at(name: string): WebGLUniformLocation | null {
    let location = this.locations.get(name)
    if (location === undefined) {
      location = this.gl.getUniformLocation(this.handle, name)
      this.locations.set(name, location)
    }
    return location
  }

  dispose(): void {
    this.gl.deleteProgram(this.handle)
    this.locations.clear()
  }
}

export interface TextureFormat {
  internalFormat: number
  format: number
  type: number
}

/** A texture plus the framebuffer that renders into it. */
export class RenderTarget {
  readonly texture: WebGLTexture
  readonly framebuffer: WebGLFramebuffer
  readonly width: number
  readonly height: number
  private readonly gl: GL

  constructor(gl: GL, width: number, height: number, format: TextureFormat) {
    this.gl = gl
    this.width = width
    this.height = height

    const texture = gl.createTexture()
    const framebuffer = gl.createFramebuffer()
    if (!texture || !framebuffer) throw new Error('could not allocate render target')

    this.texture = texture
    this.framebuffer = framebuffer

    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texImage2D(
      gl.TEXTURE_2D, 0, format.internalFormat, width, height, 0,
      format.format, format.type, null,
    )
    // NEAREST because float targets are not guaranteed to be linearly
    // filterable; the display pass interpolates in the shader instead.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    // REPEAT makes the field wrap, so the pattern has no edges to pin against.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)

    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0,
    )

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER)
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`incomplete framebuffer (0x${status.toString(16)}) at ${width}x${height}`)
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  bind(): void {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer)
    this.gl.viewport(0, 0, this.width, this.height)
  }

  dispose(): void {
    this.gl.deleteFramebuffer(this.framebuffer)
    this.gl.deleteTexture(this.texture)
  }
}

/** Two render targets that alternate roles each step. */
export class PingPong {
  read: RenderTarget
  write: RenderTarget

  constructor(gl: GL, width: number, height: number, format: TextureFormat) {
    this.read = new RenderTarget(gl, width, height, format)
    this.write = new RenderTarget(gl, width, height, format)
  }

  swap(): void {
    const previous = this.read
    this.read = this.write
    this.write = previous
  }

  dispose(): void {
    this.read.dispose()
    this.write.dispose()
  }
}

export function bindTexture(gl: GL, unit: number, texture: WebGLTexture, location: WebGLUniformLocation | null): void {
  gl.activeTexture(gl.TEXTURE0 + unit)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.uniform1i(location, unit)
}
